import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Newspaper } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { pageCopy } from '@shared/lib/pageCopy';
import { fetchNewsStands } from './api';

/**
 * News stand picker. A dark estate header over a list of newsroom cards —
 * each with cover art and an unread count. Tapping enters that stand's
 * article list (`NewsStandDetail`).
 */
export function NewsStandList(): JSX.Element {
  const copy = pageCopy().newsStand;
  const navigate = useNavigate();
  const standsQuery = useQuery({ queryKey: ['news-stands'], queryFn: fetchNewsStands });
  const stands = standsQuery.data ?? [];

  return (
    <div className="min-h-full bg-paper">
      {/* ── Header ───────────────────────────────────────────────── */}
      <HeroHeader eyebrow={copy.eyebrow} title={copy.title} subtitle={copy.subtitle} />

      {/* ── Stands ───────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 px-5 pt-4 pb-safe-or-6">
        {standsQuery.isLoading ? (
          <>
            <Skeleton className="aspect-[16/9]" />
            <Skeleton className="aspect-[16/9]" />
          </>
        ) : standsQuery.isError ? (
          <ErrorState onRetry={() => standsQuery.refetch()} />
        ) : stands.length === 0 ? (
          <EmptyState
            icon={<Newspaper className="h-7 w-7" strokeWidth={1.5} />}
            title="Quiet news week."
            description="Your community hasn't published anything yet."
          />
        ) : (
          stands.map((stand) => (
            <NewsStandCard
              key={stand.newsstand_id}
              stand={stand}
              onOpen={() => navigate(`/app/news-stands/${stand.newsstand_id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function NewsStandCard({
  stand,
  onOpen,
}: {
  stand: Awaited<ReturnType<typeof fetchNewsStands>>[number];
  onOpen: () => void;
}): JSX.Element {
  const [showCover, setShowCover] = useState(Boolean(stand.cover_url));

  return (
    <button type="button" onClick={onOpen} className="text-left">
      <Card noGrain className="overflow-hidden p-0">
        <div className="relative aspect-[16/9] bg-sunken">
          {showCover && stand.cover_url ? (
            <img
              src={stand.cover_url}
              alt=""
              className="h-full w-full object-cover"
              onError={() => setShowCover(false)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-ink-muted">
              <Newspaper className="h-8 w-8" strokeWidth={1.5} />
            </div>
          )}
          {stand.article_count && stand.article_count > 0 ? (
            <span className="absolute top-3 right-3 eyebrow rounded-pill bg-primary-600 text-[color:var(--primary-ink)] px-2 py-1">
              {stand.article_count} {stand.article_count === 1 ? 'update' : 'updates'}
            </span>
          ) : null}
        </div>
        <div className="p-4">
          <p className="font-display text-headline text-ink truncate">{stand.name}</p>
          {stand.description ? (
            <p className="text-body-sm text-ink-secondary mt-1 line-clamp-2">{stand.description}</p>
          ) : null}
        </div>
      </Card>
    </button>
  );
}
